import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Switch,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import AddSchedule from "./AddSchedule";
import {
  isAuthenticated,
  updateClassStatus,
  // updateClassTeacher, // Import the new API function
} from "../../../ApiService/ApiService";
import {
  School,
  Man,
  Adjust,
  MoreHoriz,
  Add,
  AdminPanelSettings,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchTeachersAsync,
  selectAllTeachers,
} from "../../../Slices/teachersSlice";
import "../../../Styles/dashboard.css";
import CustomDialog from "../../../Components/CustomDialog";

const headCells = [
  {
    id: "name",
    label: "Name",
    icon: <School sx={{ marginRight: 1 }} />,
  },
  {
    id: "teacher",
    label: "Class Teacher",
    icon: <Man sx={{ marginRight: 1 }} />,
  },
  { id: "status", label: "Status", icon: <Adjust sx={{ marginRight: 1 }} /> },
  {
    id: "actions",
    label: "Actions",
    icon: <AdminPanelSettings sx={{ marginRight: 1 }} />,
  },
];

function ClassesTable({
  classes,
  fetchClasses,
  handleActionClick,
  setActionComponent,
}) {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchTeachersAsync());
  }, [dispatch]);
  const teachers = useSelector(selectAllTeachers);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [paginatedData, setPaginatedData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentClassId, setCurrentClassId] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  useEffect(() => {
    setPaginatedData(classes?.data);
  }, [classes]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCurrentClassId(null);
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleStatusChange = async (id, currentStatus) => {
    try {
      if (isAuthenticated()) {
        const updatedStatus = !currentStatus;
        const response = await updateClassStatus(id, {
          isActive: updatedStatus,
        });
        if (response) {
          if (response?.data?.status === 200) {
            const updatedClassData = paginatedData?.map((classItem) =>
              classItem?.id === id
                ? { ...classItem, isActive: updatedStatus }
                : classItem
            );
            setPaginatedData(updatedClassData);
            fetchClasses();
            setDialog({
              visible: true,
              title: "Success",
              message: "Status updated successfully",
              buttons: [{ name: "OK", onPress: handleDialogClose }],
            });
          } else {
            const errorMessage =
              response.data?.errorMessage || "Something went wrong";
            setDialog({
              visible: true,
              title: "Error",
              message: errorMessage,
              buttons: [{ name: "Close", onPress: handleDialogClose }],
            });
          }
        } else {
          setDialog({
            visible: true,
            title: "Error",
            message: "Invalid response from the server",
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
        setDialog({
          visible: true,
          title: "Unauthorized action",
          message: "Please login and try again",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Something went wrong",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    }
  };

  const handleTeacherChange = async (classId, teacherId) => {
    try {
      const response = await updateClassStatus(classId, {
        classTeacherId: teacherId,
      });
      if (response && response.data?.status === 200) {
        const updatedClassData = paginatedData?.map((classItem) =>
          classItem?.id === classId
            ? { ...classItem, teacher: { id: teacherId } }
            : classItem
        );
        setPaginatedData(updatedClassData);
        setDialog({
          visible: true,
          title: "Success",
          message: "Teacher updated successfully",
          buttons: [{ name: "OK", onPress: handleDialogClose }],
        });
      } else {
        const errorMessage =
          response.data?.errorMessage || "Something went wrong";
        setDialog({
          visible: true,
          title: "Error",
          message: errorMessage,
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Something went wrong",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    }
  };

  const actionsMenu = [
    {
      label: "Add Schedule",
      icon: <Add fontSize="small" sx={{ marginRight: 1 }} />,
      onClick: () => {
        const actionComponent = (
          <AddSchedule
            classes={classes}
            setActionComponent={setActionComponent}
          />
        );
        handleActionClick(actionComponent, selectedClassId, selectedSchoolId);
      },
    },
  ];

  const slicedData = paginatedData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box>
        <Paper>
          <TableContainer>
            <Table aria-labelledby="tableTitle">
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell key={headCell.id} padding="normal">
                      <Box className="table-head">
                        {headCell.icon}
                        {headCell.label}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {slicedData?.length > 0 ? (
                  slicedData?.map((row) => (
                    <TableRow key={row?.id}>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-cell"
                      >
                        {`${row?.grade} ${row?.section ? row?.section : ""}`}
                      </TableCell>
                      <TableCell className="table-cell">
                        <FormControl
                          variant="outlined"
                          size="small"
                          sx={{ width: "50%" }}
                        >
                          <InputLabel id={`teacher-label-${row?.id}`}>
                            Teacher
                          </InputLabel>
                          <Select
                            labelId={`teacher-label-${row?.id}`}
                            id={`teacher-select-${row?.id}`}
                            value={row?.teacher?.id || ""}
                            onChange={(event) => {
                              handleTeacherChange(row?.id, event.target.value);
                            }}
                            label="Teacher"
                          >
                            {teachers?.data?.map((teacher) => (
                              <MenuItem key={teacher.id} value={teacher.id}>
                                {`${teacher.firstName} ${teacher.middleName} ${teacher.lastName}`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={row?.isActive}
                          color={row?.isActive ? "success" : "default"}
                          onClick={() =>
                            handleStatusChange(row?.id, row?.isActive)
                          }
                          inputProps={{ "aria-label": "controlled" }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(event) => {
                            setAnchorEl(event.currentTarget);
                            setCurrentClassId(row?.id);
                            setSelectedClassId(row?.id);
                            setSelectedSchoolId(row?.schoolId);
                          }}
                        >
                          <MoreHoriz />
                        </IconButton>
                        <Menu
                          id="actions-menu"
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl && currentClassId === row.id)}
                          onClose={handleClose}
                        >
                          {actionsMenu.map((action, index) => (
                            <MenuItem key={index} onClick={action.onClick}>
                              {action.icon}
                              {action.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="textSecondary">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {slicedData?.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10]}
              component="div"
              count={paginatedData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </Box>
    </>
  );
}

ClassesTable.propTypes = {
  classes: PropTypes.object.isRequired,
  fetchClasses: PropTypes.func.isRequired,
  handleActionClick: PropTypes.func.isRequired,
};

export default ClassesTable;
